import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

import { MachineMaintenanceActions } from 'redux/actions/settings/MachineMaintenanceActions';
import { MachineTypeActions } from 'redux/actions/settings/MachineTypeActions';
import { MaintenanceActions } from 'redux/actions/settings/MaintenanceActions';
import { MachinePartActions } from 'redux/actions/settings/MachinePartActions';

import { DurationInput } from 'app/components/common/DurationInput';
import { PageContentHeader } from 'app/components/common/PageContentHeader';
import Translator from 'app/components/common/Translator';
import { withRouter } from 'app/components/common/WithRouter';
import { Translate } from 'infrastructure/mapping/Translate';
import { LoginUser } from 'models/LoginUser';
import { MachineMaintenance, MachineMaintenancePart } from 'models/MachineMaintenance';
import { MachinePart } from 'models/MachinePart';
import { MachineType } from 'models/MachineType';
import { Maintenance } from 'models/Maintenance';
import { StoreModel } from 'models/StoreModel';
import { Translations } from 'models/Translations';

interface Props {
    fetchMachineMaintenances: typeof MachineMaintenanceActions.fetchMachineMaintenances;
    fetchMachineTypes: typeof MachineTypeActions.fetchMachineTypes;
    fetchMaintenances: typeof MaintenanceActions.fetchMaintenances;
    fetchMachineParts: typeof MachinePartActions.fetchMachineParts;
    addMachineMaintenance: typeof MachineMaintenanceActions.addMachineMaintenance;
    editMachineMaintenance: typeof MachineMaintenanceActions.editMachineMaintenance;
    moveToMachineMaintenances: typeof MachineMaintenanceActions.moveToMachineMaintenances;
    loginUser: LoginUser;
    machineMaintenances: MachineMaintenance[];
    machineTypes: MachineType[];
    maintenances: Maintenance[];
    machineParts: MachinePart[];
    translations: Translations;
    processing: boolean;
    match: { params: { id?: string } };
}

interface IState {
    machineMaintenance: MachineMaintenance | null; // null until the edited entry is loaded
}

type Option<T> = { label: string, value: T };

class MachineMaintenanceDetails extends React.Component<Props, IState> {
    public readonly state: IState = {
        machineMaintenance: this.initial()
    }

    componentDidMount() {
        if (this.loggedIn()) {
            if (!this.props.machineMaintenances) {
                this.props.fetchMachineMaintenances(this.props.loginUser.token);
            }
            if (!this.props.machineTypes) {
                this.props.fetchMachineTypes(this.props.loginUser.token);
            }
            if (!this.props.maintenances) {
                this.props.fetchMaintenances(this.props.loginUser.token);
            }
            if (!this.props.machineParts) {
                this.props.fetchMachineParts(this.props.loginUser.token);
            }
        }
    }

    componentDidUpdate() {
        // direct link to an edit page, the list arrives after mount
        if (!this.state.machineMaintenance && this.props.machineMaintenances) {
            // oxlint-disable-next-line react/no-did-update-set-state -- guarded: runs once when the list arrives
            this.setState({ machineMaintenance: this.initial() });
        }
    }

    initial(): MachineMaintenance | null {
        if (!this.isEdit())
            return MachineMaintenance.empty();
        const found = this.props.machineMaintenances?.find(mm => mm.id === Number(this.props.match.params.id));
        return found ? { ...found } : null;
    }

    isEdit() {
        return !!this.props.match.params.id;
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    ready() {
        return this.loggedIn() && this.state.machineMaintenance && this.props.machineTypes && this.props.maintenances && this.props.machineParts;
    }

    update(changes: Partial<MachineMaintenance>) {
        this.setState(prevState => ({ machineMaintenance: { ...prevState.machineMaintenance!, ...changes } }));
    }

    options<T extends { id: number, name: string, archivedDate?: Date }>(list: T[]): Option<T>[] {
        return list.filter(e => !e.archivedDate).map(e => ({ label: e.name, value: e }));
    }

    parts() {
        return this.state.machineMaintenance!.parts ?? [];
    }

    setPart(idx: number, changes: Partial<MachineMaintenancePart>) {
        this.update({ parts: this.parts().map((p, i) => i === idx ? { ...p, ...changes } : p) });
    }

    addPart() {
        const parts = [...this.parts(), { machinePart: MachinePart.empty(), quantity: 1 }];
        this.update({ parts });
        // focus the new row so keyboard users can pick right away
        setTimeout(() => document.getElementById('part.' + (parts.length - 1))?.focus());
    }

    removePart(idx: number) {
        this.update({ parts: this.parts().filter((_, i) => i !== idx) });
    }

    partsSection() {
        const parts = this.parts();
        return (
            <div className='c-form__sub-form'>
                <div className='c-form__label'><Translator text="Machine Parts" /></div>
                <ul className='c-form__list'>
                    {parts.map((p, idx) => {
                        // a saved part may come back with only its id, take name and unit from the part list
                        const part = this.props.machineParts.find(mp => mp.id === p.machinePart.id) ?? p.machinePart;
                        const unit = part.unit?.short;
                        const free = this.props.machineParts.filter(mp => mp.id === part.id || !parts.some(o => o.machinePart.id === mp.id));
                        return (
                            <li className='c-form__list-item' key={idx}>
                                <div className='c-form__field'>
                                    <label className='c-form__label c-form__label--required' htmlFor={'part.' + idx}><Translator text="Part" /></label>
                                    <Select
                                        inputId={'part.' + idx}
                                        value={part.id > 0 ? { label: part.name, value: part } : null}
                                        onChange={o => o && this.setPart(idx, { machinePart: o.value })}
                                        options={this.options(free)}
                                        placeholder={Translate.text(this.props.translations, "Select...")}
                                        noOptionsMessage={() => Translate.text(this.props.translations, "No options")}
                                        className='c-form__select'
                                        classNamePrefix='c-select'
                                    />
                                    <input
                                        tabIndex={-1}
                                        autoComplete='off'
                                        className='c-form__select c-form__select--input-required'
                                        value={part.id > 0 ? part.id : ''}
                                        onChange={() => { }}
                                        required
                                    />
                                </div>
                                <div className='c-form__field'>
                                    <label className='c-form__label c-form__label--required' htmlFor={'quantity.' + idx}><Translator text="quantity" /></label>
                                    <div className='c-form__input-group'>
                                        <input className='c-form__control'
                                            id={'quantity.' + idx}
                                            type='number'
                                            min={0.001} // the backend rejects 0
                                            step='any'
                                            required
                                            value={isNaN(p.quantity) ? '' : p.quantity}
                                            onChange={e => this.setPart(idx, { quantity: e.currentTarget.value === '' ? NaN : Number(e.currentTarget.value) })}
                                            autoComplete='off'
                                        />
                                        {unit && <span className='c-form__suffix'>{unit}</span>}
                                    </div>
                                </div>
                                <button
                                    type='button'
                                    className='c-button c-button--ghost c-form__list-remove'
                                    onClick={() => this.removePart(idx)}
                                    aria-label={Translate.text(this.props.translations, "Remove part") + (part.name ? ': ' + part.name : '')}
                                    title={Translate.text(this.props.translations, "Remove part")}
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                </button>
                            </li>
                        );
                    })}
                </ul>
                {parts.length < this.props.machineParts.filter(mp => !mp.archivedDate).length &&
                    <button type='button' className='c-button c-button--ghost' onClick={() => this.addPart()}>
                        <FontAwesomeIcon icon={faPlus} className='c-button__icon c-button__icon--left' />
                        <Translator text="Add part" />
                    </button>
                }
            </div>
        );
    }

    submit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const save = this.isEdit() ? this.props.editMachineMaintenance : this.props.addMachineMaintenance;
        save(this.props.loginUser.token, this.state.machineMaintenance!);
    }

    selectField<T extends { id: number, name: string }>(id: string, label: string, value: T, list: T[], onChange: (v: T) => void) {
        return (
            <div className='c-form__group'>
                <label className='c-form__label c-form__label--required' htmlFor={id}><Translator text={label} /></label>
                <Select
                    inputId={id}
                    value={value.id > 0 ? { label: value.name, value } : null}
                    onChange={o => o && onChange(o.value)}
                    options={this.options(list as (T & { archivedDate?: Date })[])}
                    placeholder={Translate.text(this.props.translations, "Select...")}
                    noOptionsMessage={() => Translate.text(this.props.translations, "No options")}
                    className='c-form__select'
                    classNamePrefix='c-select'
                />
                {/* react-select has no native required, this hidden input blocks submit while empty */}
                <input
                    tabIndex={-1}
                    autoComplete='off'
                    className='c-form__select c-form__select--input-required'
                    value={value.id > 0 ? value.id : ''}
                    onChange={() => { }}
                    required
                />
            </div>
        );
    }

    numberField(id: keyof MachineMaintenance, label: string, unit?: string) {
        const value = this.state.machineMaintenance![id] as number;
        return (
            <div className='c-form__group'>
                <label className='c-form__label c-form__label--required' htmlFor={id}><Translator text={label} />{unit ? ' (' + unit + ')' : ''}</label>
                <input className='c-form__control'
                    id={id}
                    type='number'
                    min={0}
                    step='any'
                    required
                    value={value === null || isNaN(value) ? '' : value}
                    onChange={e => this.update({ [id]: e.currentTarget.value === '' ? NaN : Number(e.currentTarget.value) })}
                    autoComplete='off'
                />
            </div>
        );
    }

    durationField(id: 'intervalDays' | 'warningThresholdDays', label: string) {
        return (
            <div className='c-form__group'>
                <label className='c-form__label c-form__label--required' htmlFor={id}><Translator text={label} /></label>
                <DurationInput
                    id={id}
                    days={this.state.machineMaintenance![id]}
                    onChange={days => this.update({ [id]: days })}
                    translations={this.props.translations}
                    required
                />
            </div>
        );
    }

    render() {
        const header = this.isEdit() ? "Edit MachineMaintenance" : "Add a new MachineMaintenance";
        if (!this.ready()) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text={header} />
                    <Translator text="Loading" />
                </div>
            )
        }
        const mm = this.state.machineMaintenance!;
        // the machine type in the list carries the unit, the one on the maintenance might not
        const serviceLifeUnit = this.props.machineTypes.find(mt => mt.id === mm.machineType.id)?.serviceLifeUnit?.short;
        return (
            <div className="c-page__content">
                <PageContentHeader text={header} />
                <div className="c-card">
                    <form className="c-form" onSubmit={e => this.submit(e)}>
                        {this.selectField('machineType', 'Machine type', mm.machineType, this.props.machineTypes, machineType => this.update({ machineType }))}
                        {this.selectField('maintenance', 'Maintenance', mm.maintenance, this.props.maintenances, maintenance => this.update({ maintenance }))}
                        {this.durationField('intervalDays', 'Time interval')}
                        {this.durationField('warningThresholdDays', 'Time before warning')}
                        {this.numberField('intervalServiceLife', 'Service life interval', serviceLifeUnit)}
                        {this.numberField('warningThresholdServiceLife', 'Service life before warning', serviceLifeUnit)}
                        {this.partsSection()}
                        <div className='c-form__group'>
                            <button className='c-button' type='button' onClick={() => this.props.moveToMachineMaintenances()}>
                                <Translator text="Cancel" />
                            </button>
                            <button className='c-button c-button--primary' type='submit' disabled={this.props.processing}>
                                {this.props.processing ? <div className="c-button__loader" /> : <Translator text="Save" />}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        machineMaintenances: state.machineMaintenances,
        machineTypes: state.machineTypes,
        maintenances: state.maintenances,
        machineParts: state.machineParts,
        translations: state.translations,
        processing: state.processingRequest
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchMachineMaintenances: bindActionCreators(MachineMaintenanceActions.fetchMachineMaintenances, dispatch),
        fetchMachineTypes: bindActionCreators(MachineTypeActions.fetchMachineTypes, dispatch),
        fetchMaintenances: bindActionCreators(MaintenanceActions.fetchMaintenances, dispatch),
        fetchMachineParts: bindActionCreators(MachinePartActions.fetchMachineParts, dispatch),
        addMachineMaintenance: bindActionCreators(MachineMaintenanceActions.addMachineMaintenance, dispatch),
        editMachineMaintenance: bindActionCreators(MachineMaintenanceActions.editMachineMaintenance, dispatch),
        moveToMachineMaintenances: bindActionCreators(MachineMaintenanceActions.moveToMachineMaintenances, dispatch),
    };
}
export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps
)(MachineMaintenanceDetails));
